// The workspace "recent views" entry — one row in the "⋯" menu in UXView's header.
//
// Backed by the `workspace_recent_views` collection (see api-service
// `app/domain/conversations/recent_view_dao.py`). Both sides derive the SAME identity from
// (cardType, contextRef, viewParams), so a view the agent opened and the identical view the
// user re-picked from the menu collapse to one entry instead of stacking duplicates.
//
// The server stores a sha1 of that identity as `entry_key`; the client uses the un-hashed
// canonical string as its local key (same inputs, same normalisation — hashing would only add an
// async SubtleCrypto round-trip for no benefit, since these keys never leave the browser).
import i18n from '@/i18n';
import type { SamModule } from '@/components/SAM/samActionTypes';

/** Where an entry came from. Mirrors the SOURCE_* constants in `recent_view_dao.py`. */
export type RecentViewSource = 'agent' | 'user_filter' | 'user_create' | 'user_open' | 'replay';

/** What a recent-views entry points at when it isn't a plain filtered list — a bound record
 *  (`property`/`engagement`/`communication`/…) or one of the singleton workflow surfaces. */
export interface RecentContextRef {
  type: string;
  id: string;
}

export interface RecentEntry {
  /** Deterministic identity — see `recentViewKey`. Stable across sessions and reloads. */
  key: string;
  /** The workspace module / surface the entry re-opens. */
  cardType: SamModule;
  /** The catalogue card that produced it (e.g. `task.list`) — part of the identity, because
   *  two different surfaces can share a module and an empty filter. */
  /** Card-catalogue id of the view (e.g. "task.list"); "" when unknown (manual filter). */
  cardDefinitionId: string;
  /** Heading shown in the menu. Re-validated from the server row on every load. */
  title: string;
  /** The one-line caption under the heading (the agent's narration, or a UI-authored summary). */
  subtitle?: string;
  contextRef?: RecentContextRef;
  /** The filter to re-apply on replay — handed straight back to the module's REST list endpoint. */
  viewParams: Record<string, unknown>;
  source: RecentViewSource;
  /** The turn whose reply produced this view, for "Tap to open chat". */
  messageId?: string;
  /** Epoch ms of the last time this view was opened — the menu's sort key. */
  lastAccessedAt: number;
}

/** Filter keys that describe pagination, not the view. Kept in sync with `_VOLATILE_PARAMS`. */
const VOLATILE_PARAMS = new Set(['skip', 'limit', 'offset', 'page']);

/** Recursively sort an arbitrary JSON value's object keys, matching Python's `json.dumps(...,
 *  sort_keys=True)` — which sorts at EVERY nesting level, not just the top one. Only the top-level
 *  call (`normalizeViewParams`) also strips pagination/empty keys; nested objects just get their
 *  keys ordered so `JSON.stringify` agrees with the server's serialization for the same value. */
function sortKeysDeep(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeysDeep);
  if (v !== null && typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj).sort()) out[k] = sortKeysDeep(obj[k]);
    return out;
  }
  return v;
}

/** Drop pagination + empty values and sort the keys (recursively — see `sortKeysDeep`), so
 *  `{status:'open'}` and `{status:'open', skip:0, assignee:''}` describe the same view, and any
 *  nested-object filter value hashes identically to the server's `json.dumps(sort_keys=True)`. */
export function normalizeViewParams(params?: Record<string, unknown> | null): Record<string, unknown> {
  if (!params) return {};
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(params).sort()) {
    const v = params[k];
    if (VOLATILE_PARAMS.has(k)) continue;
    if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) continue;
    out[k] = sortKeysDeep(v);
  }
  return out;
}

/** The entry's identity: (surface, bound record, filter). Must stay in lockstep with `_entry_key`
 *  in `recent_view_dao.py`, which hashes the same three values in the same order.
 *
 *  Deliberately NOT part of the identity: title/caption (the agent words them differently every
 *  turn — the old menu deduped on title and both over- and under-merged as a result) and the
 *  card id (contextRef already separates the surfaces that share a module, and the manual
 *  filter path has no action type to supply). */
export function recentViewKey(input: {
  cardType: string;
  contextRef?: RecentContextRef | null;
  viewParams?: Record<string, unknown> | null;
}): string {
  const ctx = input.contextRef ? `${input.contextRef.type || ''}:${input.contextRef.id || ''}` : '';
  return JSON.stringify([input.cardType || '', ctx, normalizeViewParams(input.viewParams)]);
}

/** Upsert `entry` into a newest-first list: an existing entry with the same key is replaced and
 *  moved to the front, so re-picking a view from the menu makes it the most recent one. */
export function upsertRecent(list: RecentEntry[], entry: RecentEntry): RecentEntry[] {
  const rest = list.filter((e) => e.key !== entry.key);
  return [entry, ...rest];
}

/** Fixed `contextRef.type` values every consumer here matches by literal string. Mirrors
 *  `ContextRefType` in api-service's `actions.py` — the other half of the dedup-identity
 *  contract for `context_ref.type`. Nothing enforces the two agree across languages; change them
 *  together. Referenced by `RECORD_NOUN` below and by `replayView`'s dispatch in
 *  `useChatWorkspace.ts`, instead of each hand-writing the string literal separately. */
export const CONTEXT_REF_TYPE = {
  WORKFLOW_SETTINGS: 'workflow_settings',
  COMPANY_WORKFLOWS: 'company_workflows',
  COMMUNICATION: 'communication',
  ENGAGEMENT: 'engagement',
} as const;

/** The `{type, id}` a client-recorded view is bound to. Mirrors `_RECORD_NOUN` in `actions.py`
 *  so a user-driven entry and the agent's equivalent hash to the same key. */
const RECORD_NOUN: Partial<Record<string, string>> = {
  properties: 'property',
  engagements: CONTEXT_REF_TYPE.ENGAGEMENT,
  tasks: 'task',
  my_todos: 'task',
  meetings: 'meeting',
  contacts: 'contact',
  notes: 'note',
  users: 'user',
  roles: 'role',
  communications: CONTEXT_REF_TYPE.COMMUNICATION,
  notifications: 'notification',
  workflow: CONTEXT_REF_TYPE.ENGAGEMENT,
};

export function contextRefFor(module: string, recordId?: string | null): RecentContextRef | undefined {
  if (!recordId) return undefined;
  return { type: RECORD_NOUN[module] ?? module, id: recordId };
}

// module → i18n key under chat.moduleNames (resolved at call time so a language switch applies).
const MODULE_NAME_KEYS: Partial<Record<string, string>> = {
  tasks: 'tasks', meetings: 'meetings', my_todos: 'myTodos',
  properties: 'engagements', contacts: 'contacts', notes: 'notes',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Person filters (a user id) → the phrase the list's own chips use (TaskList PRESETS). The id is
 *  usually the viewer's; anyone else reads as "a Teammate" rather than dropping the filter, which
 *  used to label "Created by Me", "Updated by Me" and "Following" all as plain "Open Tasks". */
// Values are i18n keys under chat.viewLabel.person.<key>.{mine,other,anyone}. `anyone` is used
// while the viewer's id is still unknown, so a teammate filter is never mislabelled as "Me".
const PERSON_FILTERS: Record<string, string> = {
  assignee_user_id: 'assigned',
  owner_user_id: 'created',
  updated_by_user_id: 'updated',
  follower_user_id: 'followed',
  organizer_user_id: 'organized',
  participant_user_id: 'participant',
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;

function shortDate(v: unknown): string | null {
  if (typeof v !== 'string' || !ISO_DATE_RE.test(v)) return null;
  // A bare date is a calendar day: parse it as LOCAL midnight (`new Date('YYYY-MM-DD')` is UTC
  // midnight, which reads as the previous day west of UTC).
  const d = /^\d{4}-\d{2}-\d{2}$/.test(v)
    ? new Date(+v.slice(0, 4), +v.slice(5, 7) - 1, +v.slice(8, 10))
    : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
}

/** `due_date_from` + `due_date_to` → "Due Date: Oct 4 – Oct 7" (local dates, not raw ISO). */
function dateRangeParts(f: Record<string, unknown>, used: Set<string>): string[] {
  const out: string[] = [];
  for (const k of Object.keys(f)) {
    const m = /^(.*)_(from|to)$/.exec(k);
    if (!m || used.has(k)) continue;
    const base = m[1];
    const from = shortDate(f[`${base}_from`]);
    const to = shortDate(f[`${base}_to`]);
    if (!from && !to) continue;
    used.add(`${base}_from`); used.add(`${base}_to`);
    const range = from && to ? (from === to ? from : `${from} – ${to}`) : from ? i18n.t('chat.viewLabel.from', { date: from }) : i18n.t('chat.viewLabel.until', { date: to });
    out.push(`${titleCaseWord(base)}: ${range}`);
  }
  return out;
}

function titleCaseWord(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
}

/** A short human heading for a view, derived from its module + filter.
 *
 *  Used for entries with no agent-authored title — a manual filter click, a `+` create — and as
 *  the menu's fallback. Single definition shared by the menu and the cards engine; it used to
 *  live only in `useChatWorkspace`, so user-driven views had no label at all.
 *
 *  Reads as "{qualifiers} {Module}" for the three filter shapes common across list modules
 *  (`mine`, `status`, `priority` — e.g. "My Open Tasks", "High Priority Jobs"), falling back to
 *  the old "{Module} · key: value" form for anything else so no filter combination goes unlabeled. */
export function viewLabel(module: string, params?: Record<string, unknown> | null, currentUserId?: string): string {
  const f = normalizeViewParams(params);
  const idCount = typeof f.ids === 'string' ? f.ids.split(',').filter(Boolean).length : 0;
  const nameKey = MODULE_NAME_KEYS[module];
  const plural = nameKey ? i18n.t(`chat.moduleNames.${nameKey}`) : titleCaseWord(module);
  // "1 New Task", not "1 New Tasks" (the plain module names are plurals).
  const head = idCount === 1 && plural.endsWith('s') ? plural.slice(0, -1) : plural;

  // An id-filtered list is SAM's "records I just created" view (multi-create FILTER_CREATED) —
  // name it by that, not as "Tasks · Ids: <uuid>,<uuid>".
  const words: string[] = [];
  if (idCount) words.push(i18n.t('chat.viewLabel.newCount', { count: idCount }));
  if (f.mine === true) words.push(i18n.t('chat.viewLabel.my'));
  if (typeof f.priority === 'string' && f.priority) words.push(i18n.t('chat.viewLabel.priority', { priority: titleCaseWord(f.priority) }));
  if (typeof f.status === 'string' && f.status) words.push(titleCaseWord(f.status));

  const sortBy = (f.sort_by as string) ?? '';
  const sortDir = (f.sort_dir as number) ?? -1;
  let sortLabel = '';
  if (idCount) sortLabel = '';
  else if (sortBy === 'updated_on') sortLabel = i18n.t(sortDir >= 0 ? 'chat.viewLabel.oldestUpdated' : 'chat.viewLabel.recentlyUpdated');
  else if (sortBy === 'created_on') sortLabel = i18n.t(sortDir >= 0 ? 'chat.viewLabel.oldestFirst' : 'chat.viewLabel.recentlyCreated');

  // Remaining human-readable filters — skip sort keys, ids, raw UUIDs, and whatever `words`
  // above already summarized (so e.g. `status` doesn't show up twice).
  const summarized = new Set(['mine', 'priority', 'status', 'ids']);
  const personParts: string[] = [];
  for (const [k, phrase] of Object.entries(PERSON_FILTERS)) {
    const v = f[k];
    summarized.add(k);
    if (typeof v !== 'string' || !v) continue;
    const who = !currentUserId ? 'anyone' : v === currentUserId ? 'mine' : 'other';
    // "My Open Tasks" already says assigned-to-me — don't repeat it.
    if (k === 'assignee_user_id' && who === 'mine' && f.mine === true) continue;
    personParts.push(i18n.t(`chat.viewLabel.person.${phrase}.${who}`));
  }
  const dateParts = dateRangeParts(f, summarized);
  const parts = Object.entries(f)
    .filter(([k, v]) =>
      k !== 'sort_by' && k !== 'sort_dir' &&
      !summarized.has(k) &&
      v !== false &&
      !k.endsWith('_id') &&
      !UUID_RE.test(String(v)),
    )
    // A boolean flag reads as its name ("Overdue"), not "Overdue: true". Raw filter keys stay
    // English on purpose: they are API field names with no curated translation.
    .map(([k, v]) => (v === true ? titleCaseWord(k) : `${titleCaseWord(k)}: ${v}`));

  const qualifier = [sortLabel, ...personParts, ...parts, ...dateParts].filter(Boolean).join(', ');
  if (words.length) {
    const heading = `${words.join(' ')} ${head}`;
    return qualifier ? `${heading} · ${qualifier}` : heading;
  }
  return qualifier ? `${head} · ${qualifier}` : head;
}

/** The heading to show for an entry the agent never titled. */
export function viewFallbackLabel(entry: Pick<RecentEntry, 'cardType' | 'viewParams'>, currentUserId?: string): string {
  return viewLabel(entry.cardType, entry.viewParams, currentUserId);
}
